import React, { FC, ReactNode, useMemo } from 'react';
import { Box, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Order } from 'models/order';
import { OverviewOrdersFilter } from './production-overview-utils';

type OverviewOrdersDialogFiltersProps = {
    orders: Order[];
    filter: OverviewOrdersFilter;
    onFilterChange: (filter: OverviewOrdersFilter) => void;
    stepLabelFor: (stepName: string) => string;
    /** Shown at the right end of the filter row (the dialog's count and Print button). */
    children?: ReactNode;
};

// Each filter grows to a cap, so on a full-screen dialog they stay input-sized and the
// row's right end is left for the count and Print.
const filterFieldSx = { flex: '1 1 220px', maxWidth: 340 };
// Clinic names run long, so the clinic filter is 30% wider than the others.
const clinicFieldSx = { flex: '1 1 286px', maxWidth: 442 };

const byLabel = (first: { label: string }, second: { label: string }) => first.label.localeCompare(second.label);

/**
 * DD-3880: the Workflow step list's filters that make sense on a complete, already loaded
 * list — free text, clinic and workflow step. The options come from the listed orders.
 */
const OverviewOrdersDialogFilters: FC<OverviewOrdersDialogFiltersProps> = ({
    orders,
    filter,
    onFilterChange,
    stepLabelFor,
    children,
}: OverviewOrdersDialogFiltersProps) => {
    const { t: translate } = useTranslation(['orders', 'common']);

    const customerOptions = useMemo(() => {
        const namesById = new Map<string, string>();
        orders.forEach((order) => {
            if (order.customer?._id) namesById.set(order.customer._id, order.customer.name || '');
        });
        return Array.from(namesById, ([value, label]) => ({ value, label })).sort(byLabel);
    }, [orders]);

    const stepOptions = useMemo(() => {
        const stepNames = new Set(orders.map((order) => order.workFlow?.currentStep?.name || ''));
        return Array.from(stepNames, (value) => ({ value, label: stepLabelFor(value) })).sort(byLabel);
    }, [orders]);

    return (
        <Box display="flex" alignItems="center" flexWrap="wrap" gap={1} mb={2} pt={1}>
            <TextField
                sx={filterFieldSx}
                label={translate('orders:ORDERS_FREE_TEXT_FILTER')}
                placeholder={translate('orders:ORDERS_FREE_TEXT_FILTER_PLACEHOLDER')}
                value={filter.search}
                onChange={(event) => onFilterChange({ ...filter, search: event.target.value })}
                inputProps={{ 'data-test': 'production-overview.dialog-search' }}
            />
            <FormControl sx={clinicFieldSx}>
                <InputLabel id="production-overview-dialog-clinic">{translate('common:CLINIC')}</InputLabel>
                <Select
                    labelId="production-overview-dialog-clinic"
                    label={translate('common:CLINIC')}
                    value={filter.customerId}
                    onChange={(event) => onFilterChange({ ...filter, customerId: event.target.value })}
                >
                    <MenuItem value="">{translate('common:ALL')}</MenuItem>
                    {customerOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <FormControl sx={filterFieldSx}>
                <InputLabel id="production-overview-dialog-step">{translate('orders:WORKFLOW_STEP')}</InputLabel>
                <Select
                    labelId="production-overview-dialog-step"
                    label={translate('orders:WORKFLOW_STEP')}
                    value={filter.stepName}
                    onChange={(event) => onFilterChange({ ...filter, stepName: event.target.value })}
                    data-test="production-overview.dialog-step-filter"
                >
                    <MenuItem value="">{translate('common:ALL')}</MenuItem>
                    {stepOptions
                        .filter((option) => option.value)
                        .map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                </Select>
            </FormControl>
            <Box display="flex" alignItems="center" gap={2} marginLeft="auto">
                {children}
            </Box>
        </Box>
    );
};

export default OverviewOrdersDialogFilters;
